import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { toast } from '../../../utils/utils';
import { TOAST_TYPES, DQ_PRO_PLAN_KEY, PRODUCT_KEY } from '../../../utils/constants';
import { canCreateProduct, solutionPlanKey } from '../../../utils/productAccess';
import { listOrgSchoolsService, listOrgClassesService, createProgramRequestService } from '../../../services/client.service';
import RequestLearningProgramsWizard from '../DQProgramCreator/components/shared/RequestLearningProgramsWizard.comp';
import { CreateLearningProgramDialog } from './PerformanceSummary.page';
import { openLearnCreator } from '../../../utils/learncreator';

/**
 * "Take Action" on the dashboards' Recommended Learning Programs page
 * (School Dashboard slide 34, Network Dashboard slide 33).
 *
 * Both slides carry the note "SW/SNW-P1A-Performance Summary-Take Action ·
 * SW/SNW-P1B-Performance Summary-Take Action", and Seongmin confirmed (22 Sep):
 * "For the 'Take Action,' you can use the interactions described in …
 * SW-P1A-Performance Summary-Take Action / SW-P1B-Performance Summary-Take Action".
 * So this opens exactly what Performance Summary's own Take Action opens:
 *
 *   P1A (enterprise_p1, no learning-program create)
 *       → RequestLearningProgramsWizard — Workspace deck slides 13–15:
 *         pick objectives → how should it run → "XX requests sent".
 *         Same pickers and the same createProgramRequestService submission.
 *   P1B (enterprise_p1 with learning-program create)
 *       → CreateLearningProgramDialog — Workspace deck slide 17:
 *         "Create a learning program" → Open LearnCreator (the landing page the
 *         workspace menu opens, in a new tab; the dashboard stays — QA 9 Oct, LA-SD-5).
 *
 * Any other plan: the dashboard slides name only these two flows, so nothing
 * opens (the button behaves as it did before).
 *
 * Usage: <TakeActionFlow>{(openTakeAction) => <Button onClick={openTakeAction}>…}</TakeActionFlow>
 * The plan check, picker loading and submission mirror PerformanceSummary.page.jsx
 * line for line, so both pages behave the same.
 */
const TakeActionFlow = ({ children }) => {
    const history = useHistory();
    const { authUser } = useAuth();
    const [wizardOpen, setWizardOpen] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [wizardSchools, setWizardSchools] = useState([]);
    const [wizardClasses, setWizardClasses] = useState([]);
    const [wizardGrades, setWizardGrades] = useState([]);

    const isEnterpriseP1 = solutionPlanKey(authUser?.dq_pro_plan?.plan_key) === DQ_PRO_PLAN_KEY.ENTERPRISE_P1;
    const hasLearningProgramCreate = canCreateProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM);
    const isP1A = isEnterpriseP1 && !hasLearningProgramCreate;
    const isP1B = isEnterpriseP1 && hasLearningProgramCreate;
    const isNetwork = !!authUser?.is_school_network;
    const orgCode = authUser?.org_code;

    // Wizard pickers (P1A only) — same loading as Performance Summary.
    useEffect(() => {
        if (!isP1A || !orgCode) return undefined;
        let cancelled = false;
        const orLogEmpty = (what) => (e) => {
            console.warn(`[take action] could not load ${what}:`, e?.message || e);
            return [];
        };
        Promise.all([
            listOrgSchoolsService(orgCode).catch(orLogEmpty('schools')),
            listOrgClassesService(orgCode).catch(orLogEmpty('classes')),
        ]).then(([schools, classes]) => {
            if (cancelled) return;
            const schoolRows = schools || [];
            const classRows = classes || [];
            const schoolByCode = new Map(
                schoolRows
                    .filter((r) => r.institute_code)
                    .map((r) => [r.institute_code, r.institute_name || r.org_name || r.institute_code]),
            );
            setWizardSchools(schoolRows
                .filter((r) => r.institute_name || r.org_name)
                .map((r) => ({
                    value: r.institute_name || r.org_name,
                    label: r.institute_name || r.org_name,
                    institute_code: r.institute_code || '',
                })));
            setWizardClasses(classRows
                .filter((r) => r.section_name)
                .map((r) => {
                    const school = schoolByCode.get(r.institute_code);
                    return {
                        value: r.section_name,
                        label: school ? `${r.section_name} · ${school}` : r.section_name,
                        institute_code: r.institute_code || '',
                    };
                }));
            setWizardGrades([...new Set(
                classRows.map((r) => String(r.section_level || '').trim()).filter(Boolean),
            )].sort().map((g) => ({ value: g, label: g, institute_code: '' })));
        });
        return () => { cancelled = true; };
    }, [isP1A, orgCode]);

    // One request per objective — the same submission as Performance Summary.
    const sendProgramRequests = async (objectives, details) => {
        try {
            await Promise.all(objectives.map((topic, i) => {
                const d = details[i] || {};
                const isClass = d.groupType === 'Class(es)';
                const isGrade = d.groupType === 'Grade(s)';
                const isSchool = d.groupType === 'School(s)';
                const multiSchoolCodes = (isSchool && Array.isArray(d.groupInstituteCodes) && d.groupInstituteCodes.length)
                    ? d.groupInstituteCodes
                    : null;
                return createProgramRequestService({
                    topic,
                    num_sessions: Number(d.sessions) || undefined,
                    session_duration_mins: Number(d.minutes) || undefined,
                    additional_info: String(d.additional_info || '').trim() || undefined,
                    grade_level: isGrade ? (d.groupValue || '') : '',
                    class_name: isClass ? (d.groupValue || '') : '',
                    ...(multiSchoolCodes
                        ? { institute_codes: multiSchoolCodes }
                        : { institute_code: (isClass || isSchool) ? (d.groupInstituteCode || '') : '' }),
                });
            }));
            toast(TOAST_TYPES.SUCCESS, 'Requests sent', `${objectives.length} program request${objectives.length === 1 ? '' : 's'} sent to your partner.`);
        } catch (e) {
            toast(TOAST_TYPES.ERROR, 'Could not send requests', e?.message || 'Please try again.');
        }
    };

    const openTakeAction = useCallback(() => {
        if (isP1A) setWizardOpen(true);
        else if (isP1B) setCreateDialogOpen(true);
    }, [isP1A, isP1B]);

    return (
        <>
            {children(openTakeAction)}

            {isP1A && (
                // SW/SNW-P1A-Performance Summary-Take Action.
                <RequestLearningProgramsWizard
                    open={wizardOpen}
                    onClose={() => setWizardOpen(false)}
                    partnerName={authUser?.partner_name || 'your DQ Partner'}
                    schools={wizardSchools}
                    classes={wizardClasses}
                    grades={wizardGrades}
                    isNetwork={isNetwork}
                    onSend={sendProgramRequests}
                />
            )}

            {isP1B && (
                // SW/SNW-P1B-Performance Summary-Take Action.
                <CreateLearningProgramDialog
                    open={createDialogOpen}
                    onClose={() => setCreateDialogOpen(false)}
                    onGoToLearnCreator={() => {
                        setCreateDialogOpen(false);
                        openLearnCreator(history);
                    }}
                />
            )}
        </>
    );
};

export default TakeActionFlow;
